/* eslint-disable prefer-template */

import CancelIcon from '@material-ui/icons/Cancel';
import axiosClient from 'http/axios-client';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import React, { useState, useEffect, useMemo } from 'react';
import PDFViewer from 'components/basic/dialogs/PdfViewer';
import { DialogActions, Button, Typography, Grid,
  TextField, Dialog, IconButton, makeStyles,
  FormControlLabel, Radio, RadioGroup, FormControl, FormLabel } from '@material-ui/core';
import * as _ from 'lodash';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CollectionKeys from 'hooks/molecules/collection-keys';
import { useItems } from 'hooks/atoms/items-hook';
import { Scheduler, useScheduler } from '@aldabil/react-scheduler';
import FormHelperText from '@material-ui/core/FormHelperText';
import { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import DeleteAllPopup from 'components/basic/DeleteAllPopup';
import { Edit } from '@material-ui/icons';
import { useAlerts } from '../hooks/alerts-hooks';
import MultipleAppointments from '../components/basic/MultipleAppointments';
import { useAuth } from '../AuthCtx';

const useStyles = makeStyles(() => {
  return {
    hoverCursor: {
      cursor: 'pointer !important',
    },
  };
});

export function toISOString(date) {
  const tzo = -date.getTimezoneOffset();
  const dif = 0 <= tzo ? '+' : '-';
  const pad = function (num) {
    return (10 > num ? '0' : '') + num;
  };

  return `${ date.getFullYear()
  }-${ pad(date.getMonth() + 1)
  }-${ pad(date.getDate())
  }T${ pad(date.getHours())
  }:${ pad(date.getMinutes())
  }:${ pad(date.getSeconds())
  }${ dif }${ pad(Math.floor(Math.abs(tzo) / 60))
  }:${ pad(Math.abs(tzo) % 60) }`;
}

const translateType = (type) => {
  if ('phone_call' === type) {
    return 'Phone call';
  }

  if ('video_call' === type) {
    return 'Video call';
  }

  return type;
};

const CustomEditor = ({ scheduler, handleConfirm, isLoading, data, refetch, appointments }) => {
  const { alertSuccess, alertError } = useAlerts();

  const event = scheduler.edited;
  const { user } = useAuth();
  const isBooked = 'book' === scheduler?.edited?.app_status;

  const classes = useStyles();

  const [ startDate, setStartDate ] = useState(new Date(scheduler.state.start.value));
  const [ endDate, setEndDate ] = useState(new Date(scheduler.state.end.value));

  const [ openPdfDialog, setOpenPdfDialog ] = useState(false);

  const [ serial, setSerial ] = useState('');
  const [ appointmentType, setAppointmentType ] = useState('');
  const [ pdfEx, setPdfEx ] = useState('');

  const [ serialError, setSerialError ] = useState(false);
  const [ appointmentTypeError, setAppointmentTypeError ] = useState(false);
  const [ datesError, setDatesError ] = useState(false);

  const [ deleteSeriesLoading, setDeleteSeriesLoading ] = useState(false);
  const [ openDeletePopup, setOpenDeletePopup ] = useState(false);
  const [ openEditPopup, setOpenEditPopup ] = useState(false);

  const deleteSeries = async () => {
    setDeleteSeriesLoading(true);

    return axiosClient.delete(`/appointments/batch-delete/${ scheduler.edited.parent_appointment_id.id }`)
      .then(() => {
        refetch()
          .then(() => {
            setDeleteSeriesLoading(false);
            scheduler.close();
            alertSuccess('Appointment series deleted successfuly');
          });
      })
      .catch((error) => {
        setSerialError(false);
        setAppointmentTypeError(false);
        alertError(error.response.data.message);
        scheduler.loading(false);
      });
  };

  // eslint-disable-next-line consistent-return
  const handleSubmit = async () => {
    let available = true;

    data.forEach((appointment) => {
      if (startDate.toISOString() === appointment.date_from && endDate.toISOString() === appointment.date_to) {
        available = false;
      }
    });

    const timePeriod = ((endDate - startDate) / 60000);

    if (startDate.toString() === endDate.toString()) { setDatesError(true); } else if (15 > timePeriod) { alertError('Appointment can\'t be less than 15 minutes'); } else {
      setDatesError(false);

      if (scheduler.edited !== undefined && '' !== serial && '' !== appointmentType) {
        available = true;
        const { uuid } = scheduler.edited;
        setSerialError(false);
        setAppointmentTypeError(false);
        scheduler.loading(true);
        return axiosClient.post('/appointments/bookSlot', { uuid, serial_number: serial, type: appointmentType })
          .then(() => {
            refetch();
            scheduler.close();
            scheduler.loading(false);
            alertSuccess('Appointment booked successfuly');
          })
          .catch((error) => {
            setSerialError(false);
            setAppointmentTypeError(false);
            alertError(error.response.data.message);
            scheduler.loading(false);
          });
      } if (scheduler.edited !== undefined && '' === serial && '' !== appointmentType) {
        setSerialError(true);
      } else if (scheduler.edited !== undefined && '' !== serial && '' === appointmentType) {
        setAppointmentTypeError(true);
      }

      if (available || (!available && scheduler.edited !== undefined)) {
        try {
          scheduler.loading(true);
          const newData = await handleConfirm({ start: startDate, end: endDate, data: scheduler?.edited, appointment_type: appointmentType }, event ? 'edit' : 'create');
          scheduler.onConfirm(newData, event ? 'edit' : 'create');
          scheduler.close();
        } finally {
          scheduler.loading(false);
        }
      } else if (!available && scheduler.edited === undefined) {
        scheduler.loading(false);
        scheduler.close();
        alertError('There is already an appointment for that time and day');
      }
    }
  };

  if (isLoading || null == data) {
    return null;
  }

  return (
    <div>
      {
        openPdfDialog && isBooked && (
          <Dialog
            open={ openPdfDialog }
            onClose={ () => { setOpenPdfDialog(false); } }
            fullWidth
            maxWidth='lg'
          >
            <div style={ { padding: '30px', borderRadius: '15px', position: 'relative' } }>
              <IconButton
                style={ { position: 'absolute', top: '5px', right: '5px', padding: 0, margin: 0 } }
                onClick={ () => { setOpenPdfDialog(false); } }
              >
                <CancelIcon style={ { color: 'red' } } />
              </IconButton>
              <PDFViewer serial={ scheduler.edited.serial } pdfEx={ pdfEx } />
            </div>
          </Dialog>
        )
      }

      <div style={ { padding: '1rem' } }>
        <div style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' } }>
          <Typography>
            Appointment
            <br />
            <span style={ { fontSize: '12px' } }>
              created by:
            </span>
            <br />
            <span style={ { color: '#183159', fontSize: '14px' } }>
              {(scheduler?.edited?.creator ?? user?.email)}
            </span>
          </Typography>

          {
            isBooked && !scheduler.edited.serial.startsWith('NG') && !scheduler.edited.serial.startsWith('NU') && (
              <Button
                style={ { padding: 0 } }
                startIcon={ <PictureAsPdfIcon /> }
                onClick={ () => { setOpenPdfDialog(true); } }
              >
                View pdf
              </Button>
            )
          }
          {/* If thematic package is NG or NU it has 2 pdf files (analysis and diet) so we nned 2 buttons */}
          {
            isBooked && (scheduler.edited.serial.startsWith('NG') || scheduler.edited.serial.startsWith('NU')) && (
              <div style={ { display: 'flex', gap: '25px', alignItems: 'right', marginBottom: '10px' } }>
                <Button
                  style={ { padding: 0 } }
                  startIcon={ <PictureAsPdfIcon /> }
                  onClick={ () => {
                    setPdfEx('_analysis');
                    setOpenPdfDialog(true);
                  } }
                >
                  View analysis pdf
                </Button>
                <Button
                  style={ { padding: 0 } }
                  startIcon={ <PictureAsPdfIcon /> }
                  onClick={ () => {
                    setPdfEx('_diet');
                    setOpenPdfDialog(true);
                  } }
                >
                  View diet pdf
                </Button>
              </div>
            )
          }
        </div>

        <LocalizationProvider dateAdapter={ AdapterDateFns }>
          <Grid container spacing={ 2 }>

            {
              isBooked && (
                <>
                  <Grid item xs={ 6 }>
                    <TextField
                      fullWidth
                      label='Serial number'
                      variant='outlined'
                      InputProps={ {
                        readOnly: true,
                      } }
                      value={ scheduler.edited.serial }
                    />
                  </Grid>

                  <Grid item xs={ 6 }>
                    <FormControl>
                      <FormLabel id='appointment-type-label' color='primary'>
                        <Typography>
                          Appointment type
                        </Typography>
                      </FormLabel>
                      <RadioGroup
                        aria-labelledby='appointment-type-label'
                        id='appointment-type-label'
                        row
                        defaultValue={ event.app_type }
                        onChange={ (e) => { setAppointmentType(e.target.value); } }

                      >
                        <FormControlLabel
                          value='phone_call'
                          control={ <Radio color='primary' /> }
                          label='Phone Call'
                          id='phone_call'
                        />
                        <FormControlLabel
                          value='video_call'
                          control={ <Radio color='primary' /> }
                          label='Video Call'
                          id='video_call'
                        />
                      </RadioGroup>

                    </FormControl>
                  </Grid>

                  <Grid item xs={ 6 }>
                    <TextField
                      fullWidth
                      InputProps={ {
                        readOnly: true,
                      } }
                      label='Name'
                      variant='outlined'
                      value={ scheduler.edited.name }
                    />
                  </Grid>

                  {
                    'phone_call' === scheduler.edited.app_type ? (
                      <Grid item xs={ 6 }>
                        <TextField
                          fullWidth
                          InputProps={ {
                            readOnly: true,
                          } }
                          label='Phone Number'
                          variant='outlined'
                          value={ scheduler.edited.phoneNumber }
                        />
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={ 6 }
                        className={ classes.hoverCursor }
                        style={ { position: 'relative' } }
                        onClick={ () => {
                          window.open(scheduler.edited.teamsJoinUrl);
                        } }
                      >
                        <TextField
                          fullWidth
                          InputProps={ {
                            readOnly: true,
                            style: {
                              color: '#0000EE',
                            },
                          } }
                          label='Teams join url'
                          variant='outlined'
                          value={ scheduler.edited.teamsJoinUrl }
                          style={ {
                            color: '#0000EE',
                          } }
                        />
                        <div style={ { position: 'absolute', height: '100%', width: '100%', left: 0, top: 0 } } />
                      </Grid>
                    )
                  }

                </>
              )
            }

            <Grid item xs={ 6 }>
              <DateTimePicker
                inputVariant='outlined'
                renderInput={ (inputProps) => {
                  return (<TextField { ...inputProps } variant='outlined' />);
                } }
                slotProps={ { textField: { variant: 'outlined' } } }
                autoOk
                ampm={ false }
                fullWidth
                value={ startDate }
                onChange={ setStartDate }
                label='Start'
              />
            </Grid>

            <Grid item xs={ 6 }>
              <DateTimePicker
                renderInput={ (inputProps) => {
                  return (<TextField { ...inputProps } variant='outlined' />);
                } }
                slotProps={ { textField: { variant: 'outlined' } } }
                autoOk
                inputVariant='outlined'
                fullWidth
                ampm={ false }
                value={ endDate }
                onChange={ setEndDate }
                label='End'
              />
            </Grid>
            {datesError
             && (
               <FormHelperText margin='dense' error style={ { marginTop: '-8px' } }>
                 Start and End Date should not be the same
               </FormHelperText>
             )}

          </Grid>

        </LocalizationProvider>

        {false === isBooked && event
          && (

            <Grid container spacing={ 2 } style={ { marginTop: '10px' } }>
              <Grid item xs={ 6 } md={ 12 }>
                <TextField
                  fullWidth
                  error={ serialError }
                  id='serial_number'
                  label='Serial number'
                  variant='outlined'
                  helperText={ serialError ? 'Serial number required' : '' }
                  onChange={ (e) => { setSerial(e.target.value); } }
                />
              </Grid>
              <Grid item xs={ 6 } md={ 12 }>
                <FormControl>
                  <FormLabel id='appointment-type-label' color='primary'>
                    <Typography>
                      Appointment type
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    aria-labelledby='appointment-type-label'
                    id='appointment-type-label'
                    row
                    onChange={ (e) => { setAppointmentType(e.target.value); } }

                  >
                    <FormControlLabel
                      value='phone_call'
                      control={ <Radio color='primary' /> }
                      label='Phone Call'
                      id='phone_call'
                    />
                    <FormControlLabel
                      value='video_call'
                      control={ <Radio color='primary' /> }
                      label='Video Call'
                      id='video_call'
                    />
                  </RadioGroup>

                  {appointmentTypeError
                   && (
                     <FormHelperText margin='dense' error style={ { marginTop: '-8px' } }>
                       Appointment type required
                     </FormHelperText>
                   )}

                </FormControl>
              </Grid>

            </Grid>
          )}

      </div>
      <DialogActions>
        {(scheduler?.edited?.parent_appointment_id !== undefined && null !== scheduler?.edited?.parent_appointment_id)
            && (
              <Grid container>
                <Grid item xs={ 8 }>
                  <LoadingButtonNoGrid
                    style={ { maxWidth: '100%', color: 'red' } }
                    labelColor='red'
                    loading={ deleteSeriesLoading }
                    label='Delete Series'
                    onClick={ () => { return setOpenDeletePopup(true); } }
                    deleteIcon
                  />
                  <Button onClick={ () => { return setOpenEditPopup(true); } }>
                    <Edit style={ { marginRight: '6px' } } />
                    Edit Series
                  </Button>
                </Grid>
                <Grid item xs={ 4 } style={ { display: 'flex', justifyContent: 'flex-end' } }>
                  <Button onClick={ scheduler.close }>Cancel</Button>
                  <Button onClick={ handleSubmit }>Confirm</Button>
                </Grid>
              </Grid>
            )}
        {(scheduler?.edited?.parent_appointment_id === undefined || null === scheduler?.edited?.parent_appointment_id)
            && (
              <Grid item>
                <Button onClick={ scheduler.close }>Cancel</Button>
                <Button onClick={ handleSubmit }>Confirm</Button>
              </Grid>
            )}
      </DialogActions>
      {true === openDeletePopup
      && (
        <DeleteAllPopup
          deleteSeries={ deleteSeries }
          setOpenDeletePopup={ setOpenDeletePopup }
          openDeletePopup={ openDeletePopup }
        />
      )}

      {true === openEditPopup
      && (
        <MultipleAppointments
          setOpenDialog={ setOpenEditPopup }
          openDialog={ openEditPopup }
          refetch={ refetch }
          appointments={ appointments }
          info={ scheduler?.edited?.parent_appointment_id }
          close={ scheduler }

        />
      )}
    </div>
  );
};

const AppointmentsPage = () => {
  const { setEvents, selectedDate } = useScheduler();
  const currentDate = new Date(selectedDate);
  const startOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1); // 1st day of the previous month
  const endOfNextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0); // Last day of the next month
  const { user } = useAuth();

  const {
    items: data,
    itemsLoading: isLoading,
    deleteItem,
    refetch,
    createItem,
  } = useItems(CollectionKeys.APPOINTMENTS, {
    filterQuery: {
      _limit: -1,
      date_from_gte: startOfLastMonth,
      date_to_lte: endOfNextMonth,
    },
  });

  const [ multipleAppointmentsDialogOpen, setMultipleAppointmentsDialogOpen ] = useState(false);

  const appointments = useMemo(() => {
    return data?.map((d) => {
      const isOpen = 'open' === d.status;

      const title = isOpen ? 'Open' : translateType(d.type) + ': ' + d?.serial_number?.serial;
      const name = isOpen ? undefined : d.customer?.name + ' ' + d.customer.family_name;

      return {
        event_id: d.id,
        title,
        start: new Date(d.date_from),
        end: new Date(d.date_to),
        uuid: d.uuid,
        parent_appointment_id: d.parent_appointment_id,
        color: isOpen ? '#93C120' : 'phone_call' === d.type ? '#af53ed' : '#648DAE',
        serial: isOpen ? undefined : d?.serial_number?.serial ?? undefined,
        app_type: !isOpen ? d.type : undefined,
        app_status: d.status,
        phoneNumber: isOpen ? undefined : d.customer?.phone_number,
        teamsJoinUrl: d.join_call_url,
        name,
        creator: d?.admin_tool_user?.email,
      };
    });
  }, [ data, isLoading ]);

  useEffect(() => {
    if (null != appointments) {
      setEvents(appointments);
    }
  }, [ appointments ]);

  if (isLoading || null == data) {
    return null;
  }

  const deleteAppointment = async (id) => {
    return deleteItem(id).then(() => {
      return id;
    });
  };

  const handleConfirm = async (e, action) => {
    if ('edit' === action) {
      const endpoint = `${ CollectionKeys.APPOINTMENTS }/${ e.data.event_id }`;

      return axiosClient.put(endpoint, { date_from: toISOString(e.start), date_to: toISOString(e.end), appointment_type: e.appointment_type }).then(() => {
        refetch();
        return {
          event_id: e.data.event_id,
          title: e.data.title,
          start: e.start,
          end: e.end,
          uuid: e.data.uuid,
          parent_appointment_id: e.parent_appointment_id,
          color: e.data.color,
          serial: e.data.serial,
          app_type: e.data.app_type,
          app_status: e.data.status,
          phoneNumber: e.data.phoneNumber,
          teamsJoinUrl: e.teamsJoinUrl,
          name: e.data.name,
        };
      });
    }

    return createItem({ date_from: toISOString(e.start), date_to: toISOString(e.end) }).then((dd) => {
      refetch();
      return {
        event_id: dd.id,
        title: 'Open',
        start: new Date(dd.date_from),
        end: new Date(dd.date_to),
        uuid: dd.uuid,
        parent_appointment_id: dd.parent_appointment_id,
        color: '#93C120',
        serial: undefined,
        app_type: dd.type,
        app_status: dd.status,
        phoneNumber: undefined,
        name: undefined,
        teamsJoinUrl: undefined,
        creator: user.email,
      };
    });
  };

  return (
    <div>
      {
        multipleAppointmentsDialogOpen && (
          <MultipleAppointments
            setOpenDialog={ setMultipleAppointmentsDialogOpen }
            openDialog={ multipleAppointmentsDialogOpen }
            refetch={ refetch }
            appointments={ appointments }
            info=''
          />
        )
      }

      <div style={ { display: 'flex', justifyContent: 'center' } }>
        <Button
          variant='contained'
          color='secondary'
          style={ { color: 'white' } }
          onClick={ () => { setMultipleAppointmentsDialogOpen(true); } }
        >
          Add multiple appointments
        </Button>
      </div>
      <Scheduler
        eventRenderer={ (e) => { return <MyEvent event={ e } />; } }
        timeZone='Europe/Athens'
        dialogMaxWidth='sm'
        onDelete={ deleteAppointment }
        onConfirm={ handleConfirm }
        customEditor={ (scheduler) => {
          return (
            <CustomEditor
              scheduler={ scheduler }
              handleConfirm={ handleConfirm }
              isLoading={ isLoading }
              data={ data }
              refetch={ refetch }
              appointments={ appointments }

            />
          );
        } }
        viewerExtraComponent={ (fields, event) => {
          return (
            <div>
              <Typography style={ { fontSize: '12px', color: 'darkgray' } }>
                created by:
                {' '}
                <span style={ { color: '#183159', fontSize: '13px' } }>
                  {event?.creator}
                </span>
              </Typography>
            </div>
          );
        } }
        draggable={ false }
        month={
          {
            weekDays: [ 1, 2, 3, 4, 5 ],
            weekStartOn: 0,
            startHour: 9,
            endHour: 20,
            step: 60,
            navigation: true,
          }
        }
        week={
          {
            weekDays: [ 1, 2, 3, 4, 5 ],
            weekStartOn: 0,
            startHour: 9,
            endHour: 20,
            step: 60,
            navigation: true,
          }
        }
        hourFormat='24'
        view='week'
        events={ appointments }
      />
    </div>
  );
};

const MyEvent = ({ event }) => {
  return (
    <div
      style={ {
        borderRadius: 6,
        paddingTop: '2px',
        paddingBottom: '4px',
        alignItems: 'center',
        fontSize: '12px',
      } }
    >
      <span style={ { fontSize: '12px' } }>
        <strong>{event.title}</strong>
      </span>
      <div style={ { marginTop: '4px' } }>
        created by:
        {' '}
        <strong>{event?.creator.slice(0, event?.creator.indexOf('@'))}</strong>
      </div>
      <div style={ { marginTop: '4px' } }>
        {event.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        {' '}
        -
        {event.end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

export default AppointmentsPage;
